import React, { useState } from 'react';
import { Container, Card, Nav, Tab, Button } from 'react-bootstrap';
import ServiceSelection from './tabs/ServiceSelection';
import BarberSelection from './tabs/BarberSelection';
import DateTimeSelection from './tabs/DateTimeSelection';
import CustomerInfo from './tabs/CustomerInfo';
import BookingSummary from './BookingSummary';
import './BookingForm.css';

const BookingForm = ({ corporationId }) => {
  const [activeTab, setActiveTab] = useState('services');
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectedBarber, setSelectedBarber] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [customerData, setCustomerData] = useState({
    name: '',
    phone: '',
    email: '',
    notes: ''
  });

  // Handle service selection
  const handleServiceSelect = (service) => {
    const isSelected = selectedServices.some(s => s.id === service.id);
    if (isSelected) {
      setSelectedServices(selectedServices.filter(s => s.id !== service.id));
    } else {
      setSelectedServices([...selectedServices, service]);
    }
  };

  // Handle barber selection
  const handleBarberSelect = (barber) => {
    setSelectedBarber(barber);
  };

  // Handle date/time selection
  const handleDateTimeSelect = (date, time) => {
    setSelectedDate(date);
    setSelectedTime(time);
  };

  // Handle customer info update
  const handleCustomerInfoChange = (field, value) => {
    setCustomerData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Navigation handlers
  const handleNext = () => {
    const tabs = ['services', 'professional', 'datetime', 'customer', 'summary'];
    const currentIndex = tabs.indexOf(activeTab);
    if (currentIndex < tabs.length - 1) {
      setActiveTab(tabs[currentIndex + 1]);
    }
  };

  const handlePrevious = () => {
    const tabs = ['services', 'professional', 'datetime', 'customer', 'summary'];
    const currentIndex = tabs.indexOf(activeTab);
    if (currentIndex > 0) {
      setActiveTab(tabs[currentIndex - 1]);
    }
  };

  // Validation for each step
  const canProceedToProfessional = selectedServices.length > 0;
  const canProceedToDateTime = selectedBarber !== null;
  const canProceedToCustomer = selectedDate && selectedTime;
  const canProceedToSummary = customerData.name && customerData.phone;

  // Check if tab should be enabled
  const isTabEnabled = (tabName) => {
    switch (tabName) {
      case 'professional':
        return canProceedToProfessional;
      case 'datetime':
        return canProceedToDateTime;
      case 'customer':
        return canProceedToCustomer;
      case 'summary':
        return canProceedToSummary;
      default:
        return true;
    }
  };

  // Handle tab selection with validation
  const handleTabSelect = (tabKey) => {
    if (tabKey && isTabEnabled(tabKey)) {
      setActiveTab(tabKey);
    }
  };

  // Tab configuration
  const tabs = ['services', 'professional', 'datetime', 'customer', 'summary'];
  const tabLabels = ['Services', 'Barbers', 'Date & Time', 'Your Info', 'Summary'];
  const tabIcons = ['bi-scissors', 'bi-person-badge', 'bi-calendar-check', 'bi-person', 'bi-check-circle'];
  const currentTabIndex = tabs.indexOf(activeTab);
  const isFirstTab = currentTabIndex === 0;
  const isLastTab = currentTabIndex === tabs.length - 1;

  return (
    <Container className="booking-form-container">
      <div className="mb-3">
        <h1>Book Appointment</h1>
        <p className="lead">Complete all steps to confirm your booking</p>
      </div>

      <Card>
        <Card.Body>
          <Tab.Container activeKey={activeTab} onSelect={handleTabSelect}>
            <div className="tab-navigation-wrapper">
              <Nav variant="tabs" className="custom-tabs">
                {tabs.map((tab, index) => (
                  <Nav.Item key={tab}>
                    <Nav.Link 
                      eventKey={tab}
                      disabled={!isTabEnabled(tab) && activeTab !== tab}
                      className={activeTab === tab ? 'active' : ''}
                    >
                      <i className={`${tabIcons[index]} tab-icon`}></i>
                      <span className="tab-label">{tabLabels[index]}</span>
                    </Nav.Link>
                  </Nav.Item>
                ))}
              </Nav>
              <div className="tab-navigation-buttons">
                {!isFirstTab && (
                  <Button
                    variant="outline-secondary"
                    onClick={handlePrevious}
                    className="me-2 nav-arrow-button"
                  >
                    <i className="bi bi-arrow-left"></i>
                    <span className="button-text">Back</span>
                  </Button>
                )}
                {!isLastTab && (
                  <Button
                    variant="secondary"
                    onClick={handleNext}
                    disabled={
                      (activeTab === 'services' && !canProceedToProfessional) ||
                      (activeTab === 'professional' && !canProceedToDateTime) ||
                      (activeTab === 'datetime' && !canProceedToCustomer) ||
                      (activeTab === 'customer' && !canProceedToSummary)
                    }
                    className="next-button-custom nav-arrow-button"
                  >
                    <span className="button-text">Next</span>
                    <i className="bi bi-arrow-right"></i>
                  </Button>
                )}
              </div>
            </div>

            <Tab.Content className="mt-3">
              <Tab.Pane eventKey="services">
                <ServiceSelection
                  selectedServices={selectedServices}
                  onServiceSelect={handleServiceSelect}
                  corporationId={corporationId}
                />
              </Tab.Pane>

              <Tab.Pane eventKey="professional">
                <BarberSelection
                  selectedBarber={selectedBarber}
                  onBarberSelect={handleBarberSelect}
                  corporationId={corporationId}
                />
              </Tab.Pane>

              <Tab.Pane eventKey="datetime">
                <DateTimeSelection
                  selectedDate={selectedDate}
                  selectedTime={selectedTime}
                  onDateTimeSelect={handleDateTimeSelect}
                  selectedBarber={selectedBarber}
                  selectedServices={selectedServices}
                  corporationId={corporationId}
                />
              </Tab.Pane>

              <Tab.Pane eventKey="customer">
                <CustomerInfo
                  customerData={customerData}
                  onCustomerInfoChange={handleCustomerInfoChange}
                />
              </Tab.Pane>

              <Tab.Pane eventKey="summary">
                <BookingSummary
                  selectedServices={selectedServices}
                  selectedBarber={selectedBarber}
                  selectedDate={selectedDate}
                  selectedTime={selectedTime}
                  customerData={customerData}
                  corporationId={corporationId}
                />
              </Tab.Pane>
            </Tab.Content>
          </Tab.Container>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default BookingForm;
